// Lifecycle (DESIGN_SPEC §10.13, §12.1): job.state transitions → the six display steps
// QUEUED · PROVISIONING (includes dispatched) · BOOTING · RUNNING (T0) · COLLECTING · COMPLETED.
// A terminal failure or cancel replaces the step after the last one reached; nothing follows it.
//   lifecycle(transitions, state, t0Source) → LifeStep[]
//   phaseOf(transitions, state) → the display phase a run was in when it stopped (or is in now)
//   PHASE_WORD[phase]          → prose for "during {word}" ("boot", "the run", …)
import type { EffectiveState, Transition } from "@/lib/derive/types";

export type DisplayState = "queued" | "provisioning" | "booting" | "running" | "collecting" | "completed";
const DISPLAY: readonly DisplayState[] = ["queued", "provisioning", "booting", "running", "collecting", "completed"];

type StepStatus = "done" | "current" | "waiting" | "future" | "stopped";
export type LifeStep = {
  state: DisplayState | "failed" | "cancelled";
  status: StepStatus;
  /** Epoch ms the step began; null when unknown (passed through without an event) or still ahead. */
  ts: number | null;
  /** Start of the next known step (done steps), for the duration. */
  endTs: number | null;
  reason?: string;
  t0?: boolean;
  /** Known only from the job record (the stream never delivered this step). */
  fromRecord?: boolean;
};

export const PHASE_WORD: Record<DisplayState, string> = {
  queued: "the queue",
  provisioning: "provisioning",
  booting: "boot",
  running: "the run",
  collecting: "collection",
  completed: "collection",
};

export function displayOf(s: EffectiveState | string): DisplayState | null {
  if (s === "dispatched") return "provisioning";
  return (DISPLAY as readonly string[]).includes(s) ? (s as DisplayState) : null;
}

const finite = (n: number | null | undefined): n is number => typeof n === "number" && Number.isFinite(n);

type Reached = { ts: number | null; reason?: string; fromRecord?: boolean };

function reachedMap(transitions: readonly Transition[]) {
  const reached = new Map<DisplayState, Reached>();
  let terminal: Transition | undefined;
  for (const t of transitions) {
    if (t.state === "failed" || t.state === "cancelled") {
      if (!terminal) terminal = t;
      continue;
    }
    const d = displayOf(t.state);
    if (!d) continue;
    const cur = reached.get(d);
    if (!cur) reached.set(d, { ts: finite(t.ts) ? t.ts : null, reason: t.reason, fromRecord: t.source === "record" });
    else if (cur.ts === null && finite(t.ts)) cur.ts = t.ts;
  }
  return { reached, terminal };
}

/** The display phase the run was in when it stopped (terminal) or is in now (live). */
export function phaseOf(transitions: readonly Transition[], state: EffectiveState): DisplayState {
  const { reached } = reachedMap(transitions);
  let last: DisplayState = "queued";
  for (const d of DISPLAY) if (reached.has(d) && d !== "completed") last = d;
  const own = displayOf(state);
  if (own && own !== "completed" && DISPLAY.indexOf(own) > DISPLAY.indexOf(last)) return own;
  return last;
}

export function lifecycle(
  transitions: readonly Transition[],
  state: EffectiveState,
  t0Source: "running" | "booting" | null,
): LifeStep[] {
  const { reached, terminal } = reachedMap(transitions);
  const stopped = state === "failed" || state === "cancelled";
  const own = displayOf(state);
  if (own && !reached.has(own)) reached.set(own, { ts: null });

  let lastIdx = 0;
  DISPLAY.forEach((d, i) => {
    if (reached.has(d)) lastIdx = i;
  });

  const steps: LifeStep[] = [];
  for (let i = 0; i < DISPLAY.length; i++) {
    const d = DISPLAY[i];
    if (stopped && i > lastIdx) break;
    const r = reached.get(d);
    let status: StepStatus;
    if (i < lastIdx) status = "done";
    else if (i === lastIdx) status = stopped || state === "completed" ? "done" : state === "queued" ? "waiting" : "current";
    else status = "future";
    steps.push({
      state: d,
      status,
      ts: r?.ts ?? null,
      endTs: null,
      reason: r?.reason || undefined,
      t0: t0Source !== null && d === t0Source,
      fromRecord: r?.fromRecord,
    });
  }
  if (stopped) {
    steps.push({
      state: state as "failed" | "cancelled",
      status: "stopped",
      ts: terminal && finite(terminal.ts) ? terminal.ts : null,
      endTs: null,
      reason: terminal?.reason || undefined,
      fromRecord: terminal?.source === "record",
    });
  }
  // a done step lasts until the next step with a known start; a stop only the record knows (the stream
  // never delivered it) says when the record closed, not when the step ended
  for (let i = 0; i < steps.length; i++) {
    if (steps[i].status !== "done" || steps[i].ts === null) continue;
    for (let j = i + 1; j < steps.length; j++) {
      const ts = steps[j].ts;
      if (ts !== null) {
        if (!(steps[j].fromRecord && !steps[i].fromRecord)) steps[i].endTs = ts;
        break;
      }
    }
  }
  return steps;
}
